<div class="card ticket-form" [class.is-dialog]="isDialog">
    <!-- Header -->
    <div class="ticket-form-header">
        <div class="d-flex align-items-center gap-4">
            <div class="header-icon">
                <i class="fa-duotone fa-solid fa-headset"></i>
            </div>
            <div class="flex-grow-1">
                <h1 class="fs-2 fw-bolder text-dark mb-1">{{ 'WHERE_DO_YOU_NEED_HELP' | translate }}</h1>
                <div class="fs-6 text-muted">{{ 'TICKET_FORM_SUBTITLE' | translate }}</div>
            </div>
            <button *ngIf="isDialog" type="button" class="btn btn-icon btn-sm btn-active-light-primary" [attr.aria-label]="'CLOSE' | translate" (click)="close()">
                <i class="fa-solid fa-xmark fs-2"></i>
            </button>
        </div>

        <!-- Stepper -->
        <ol class="tf-stepper">
            <li *ngFor="let item of steps; let i = index" class="tf-step"
                [class.active]="item.step === currentStep"
                [class.completed]="item.step < currentStep">
                <button type="button" class="tf-step-button" [disabled]="item.step >= currentStep" (click)="goToStep(item.step)">
                    <span class="tf-step-index">
                        <i *ngIf="item.step < currentStep; else stepNumber" class="fa-solid fa-check"></i>
                        <ng-template #stepNumber>{{ i + 1 }}</ng-template>
                    </span>
                    <span class="tf-step-label">{{ item.label | translate }}</span>
                </button>
            </li>
        </ol>
    </div>

    <!-- Body -->
    <div class="ticket-form-body">

        <!-- Step 1 / 2: category and subcategory -->
        <ng-container *ngIf="currentStep !== Step.Details">
            <div class="fs-6 fw-semibold text-gray-700 mb-4">{{ 'SELECT_ONE_OPTION' | translate }}</div>

            <div class="option-grid" role="radiogroup">
                <ng-container *ngIf="currentStep === Step.Category">
                    <button *ngFor="let type of ticketTypes; trackBy: trackById" type="button" role="radio"
                        class="option-card" [class.active]="selectedTypeId === type.id" [attr.aria-checked]="selectedTypeId === type.id"
                        (click)="selectType(type.id)" (dblclick)="nextStep()">
                        <span class="option-radio"></span>
                        <span class="option-label">{{ 'INVARIANT_DESCRIPTIONS.' + type.invariantCultureDescription | translate }}</span>
                    </button>
                </ng-container>

                <ng-container *ngIf="currentStep === Step.Subcategory">
                    <button *ngFor="let subtype of ticketSubtypes; trackBy: trackById" type="button" role="radio"
                        class="option-card" [class.active]="selectedSubtypeId === subtype.id" [attr.aria-checked]="selectedSubtypeId === subtype.id"
                        (click)="selectedSubtypeId = subtype.id" (dblclick)="nextStep()">
                        <span class="option-radio"></span>
                        <span class="option-label">{{ 'INVARIANT_DESCRIPTIONS.' + subtype.invariantCultureDescription | translate }}</span>
                    </button>
                </ng-container>
            </div>

            <div *ngIf="(currentStep === Step.Category ? ticketTypes : ticketSubtypes).length === 0" class="empty-state">
                <i class="fa-duotone fa-solid fa-folder-open fs-2x mb-3"></i>
                <span>{{ 'NO_OPTIONS_AVAILABLE' | translate }}</span>
            </div>
        </ng-container>

        <!-- Step 3: details -->
        <form *ngIf="currentStep === Step.Details" [formGroup]="ticketForm" class="d-flex flex-column gap-7">

            <!-- Association -->
            <section *ngIf="isDialog && dialogData; else associationFields">
                <div class="section-title">{{ 'LINKED_TO' | translate }}</div>
                <div class="d-flex flex-wrap gap-2">
                    <span *ngFor="let contract of dialogData.contracts" class="link-chip">
                        <i class="fa-solid fa-file-contract"></i> {{ contract.number }}
                    </span>
                    <span *ngFor="let supply of dialogData.electricitySupplies" class="link-chip">
                        <i class="fa-solid fa-bolt"></i> {{ supply.code }}
                    </span>
                    <span *ngFor="let supply of dialogData.naturalGasSupplies" class="link-chip">
                        <i class="fa-solid fa-fire-flame-simple"></i> {{ supply.code }}
                    </span>
                </div>
            </section>

            <ng-template #associationFields>
                <section class="row g-5">
                    <div class="col-6">
                        <label class="form-label required">{{ 'ASSOCIATION_TYPE' | translate }}</label>
                        <is-select [Options]="associationTypeOptions" [menuFitInput]="true" [solid]="false" formControlName="associationType"></is-select>
                        <div *ngIf="ticketForm.controls.associationType.touched && ticketForm.controls.associationType.invalid" class="field-error">
                            {{ 'FIELD_REQUIRED' | translate }}
                        </div>
                    </div>
                    <div class="col-6"></div>
                    <div class="col-md-6" *ngIf="associationType != null && associationType !== AssociationType.Entity">
                        <label class="form-label required">{{ 'CONTRACT' | translate }}</label>
                        <is-multi-select formControlName="contract" [DataSource$]="contractsDatasource" [httpMethod]="'POST'"
                            [requestOnFocus]="true" [queryable]="true" [menuHasSameWidthAsInput]="true" [solidInput]="false"
                            (onSelectionChange)="onContractsChanged($event)"></is-multi-select>
                        <div *ngIf="ticketForm.controls.contract.touched && ticketForm.controls.contract.invalid" class="field-error">
                            {{ 'FIELD_REQUIRED' | translate }}
                        </div>
                    </div>
                    <div class="col-md-6" [hidden]="associationType !== AssociationType.Cpe">
                        <label class="form-label required">{{ 'CPE' | translate }}</label>
                        <is-multi-select formControlName="cpe" [options]="cpeOptions" [selectableParents]="false" [externalLoading]="suppliesLoading"
                            [menuHasSameWidthAsInput]="true" [solidInput]="false"></is-multi-select>
                        <div *ngIf="ticketForm.controls.cpe.touched && ticketForm.controls.cpe.invalid" class="field-error">
                            {{ 'FIELD_REQUIRED' | translate }}
                        </div>
                    </div>

                    <div class="col-md-6" [hidden]="associationType !== AssociationType.Cui">
                        <label class="form-label required">{{ 'CUI' | translate }}</label>
                        <is-multi-select formControlName="cui" [options]="cuiOptions" [externalLoading]="suppliesLoading"
                            [menuHasSameWidthAsInput]="true" [solidInput]="false"></is-multi-select>
                        <div *ngIf="ticketForm.controls.cui.touched && ticketForm.controls.cui.invalid" class="field-error">
                            {{ 'FIELD_REQUIRED' | translate }}
                        </div>
                    </div>
                </section>
            </ng-template>

            <!-- Description -->
            <section>
                <label for="ticket-description" class="form-label required">{{ 'DESCRIBE_SUPPORT_REQUEST' | translate }}</label>
                <textarea id="ticket-description" formControlName="description" rows="5" class="form-control description-input"
                    [class.is-invalid]="ticketForm.controls.description.touched && ticketForm.controls.description.invalid"></textarea>
                <div *ngIf="ticketForm.controls.description.touched && ticketForm.controls.description.invalid" class="field-error">
                    {{ 'FIELD_REQUIRED' | translate }}
                </div>
            </section>

            <!-- Attachments -->
            <section>
                <div class="section-title">
                    {{ 'ATTACHMENTS' | translate }}
                    <span *ngIf="files.length > 0" class="badge badge-light-primary ms-2">{{ files.length }}</span>
                </div>

                <label class="tf-dropzone" [class.drag-over]="isDragOver"
                    (dragover)="onDragOver($event)" (dragleave)="onDragLeave()" (drop)="onDrop($event)">
                    <input #fileInput type="file" class="visually-hidden" multiple [accept]="supportedFileTypes.join(',')" (change)="onFilesSelected(fileInput)">
                    <i class="fa-duotone fa-solid fa-cloud-arrow-up tf-dropzone-icon"></i>
                    <span class="fw-semibold text-gray-800">{{ 'DRAG_OR_SELECT_FILES' | translate }}</span>
                    <span class="fs-7 text-muted">{{ 'FILE_INPUT_AREA_DESCRIPTION' | translate: { inputTypes: supportedFileTypesLabel } }}</span>
                </label>

                <ul *ngIf="files.length > 0" class="file-list">
                    <li *ngFor="let file of files; trackBy: trackByFileName" class="file-item">
                        <i class="fa-duotone fa-solid file-icon" [ngClass]="getFileIcon(file)"></i>
                        <div class="file-info">
                            <span class="file-name" [title]="file.name">{{ file.name }}</span>
                            <span class="fs-8 text-muted">{{ formatFileSize(file.size) }}</span>
                        </div>
                        <button *ngIf="canPreviewFile(file)" type="button" class="btn btn-icon btn-sm btn-active-light-primary"
                            [title]="'VIEW' | translate" (click)="viewFile(file)">
                            <i class="fa-solid fa-eye"></i>
                        </button>
                        <button type="button" class="btn btn-icon btn-sm btn-active-light-danger"
                            [title]="'REMOVE' | translate" (click)="removeFile(file)">
                            <i class="fa-solid fa-trash-can"></i>
                        </button>
                    </li>
                </ul>
            </section>
        </form>
    </div>

    <!-- Footer -->
    <div class="ticket-form-footer">
        <button *ngIf="currentStep !== Step.Category" type="button" class="btn btn-light" [disabled]="isSubmitting" (click)="previousStep()">
            <i class="fa-solid fa-arrow-left me-2"></i>{{ 'PREVIOUS' | translate }}
        </button>

        <button type="button" class="btn btn-primary ms-auto" [disabled]="!canContinue" (click)="nextStep()">
            <ng-container *ngIf="currentStep !== Step.Details; else submitLabel">
                {{ 'CONTINUE' | translate }}<i class="fa-solid fa-arrow-right ms-2"></i>
            </ng-container>
            <ng-template #submitLabel>
                <span *ngIf="isSubmitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
                {{ 'SUBMIT' | translate }}<i *ngIf="!isSubmitting" class="fa-solid fa-paper-plane ms-2"></i>
            </ng-template>
        </button>
    </div>
</div>
